import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import CourseCard from "@/components/CourseCard";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, BookOpen, Trophy, Clock } from "@phosphor-icons/react";

export default function Dashboard() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/enrollments");
        setEnrollments(data);
      } catch (e) {
        setError(formatApiError(e, "Could not load your courses."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = enrollments.length;
  const inProgress = enrollments.filter((e) => e.progress.percent > 0 && e.progress.percent < 100).length;
  const completed = enrollments.filter((e) => e.progress.percent === 100).length;

  return (
    <AppLayout
      title="My Learning"
      eyebrow="Welcome back"
      actions={
        <Link
          to="/catalog"
          className="px-4 py-2 bg-[#E25822] hover:bg-[#C44E1A] text-white text-sm font-medium inline-flex items-center gap-2"
          data-testid="dashboard-browse-catalog"
        >
          Browse catalog <ArrowRight size={16} weight="bold" />
        </Link>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 -mr-px mb-10">
        {[
          { label: "Enrolled courses", value: total, icon: BookOpen },
          { label: "In progress", value: inProgress, icon: Clock },
          { label: "Completed", value: completed, icon: Trophy },
        ].map((s) => (
          <div
            key={s.label}
            className="border border-[#EAEAE6] -mr-px p-6 bg-white"
            data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">{s.label}</div>
                <div className="font-heading text-5xl font-semibold tracking-tight mt-2">{s.value}</div>
              </div>
              <s.icon size={28} weight="duotone" color="#E25822" />
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="text-sm text-[#993333] border-l-2 border-[#993333] pl-3 py-1 bg-red-50 mb-6">{error}</div>
      )}

      {/* My courses */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="eyebrow">Continue learning</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">My courses</h2>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A8A85]">Loading your courses...</div>
      ) : enrollments.length === 0 ? (
        <div className="border border-dashed border-[#D8D8D2] p-10 text-center" data-testid="dashboard-empty-state">
          <div className="eyebrow mb-3">Empty</div>
          <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">No enrollments yet</h3>
          <p className="text-[#5C5C58] mb-6">Browse the catalog and enrol in your first course.</p>
          <Link
            to="/catalog"
            className="px-6 py-3 bg-[#E25822] hover:bg-[#C44E1A] text-white text-sm font-medium inline-flex items-center gap-2"
            data-testid="dashboard-empty-cta"
          >
            Browse catalog <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {enrollments.map((e) => (
            <CourseCard key={e.id} course={e.course} progress={e.progress} />
          ))}
        </div>
      )}
    </AppLayout>
  );
}
